/**
 * WorkflowHierarchyTree
 *
 * For batch sprints — shows the parent->child workflow tree.
 * BatchCoordinator at root with IssueLifecycle children.
 * Click a node to select that workflow for waterfall + event table.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, GitBranch, Activity, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import type { ChildWorkflowNode } from '../../api/telemetry';

interface WorkflowHierarchyTreeProps {
  tree: ChildWorkflowNode;
  selectedWorkflowId: string;
  onSelectWorkflow: (workflowId: string) => void;
  className?: string;
}

/* ── Status Config ────────────────────────────────────────────────── */

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; color: string; bg: string }> = {
  running:    { icon: <Loader2 size={12} className="animate-spin" />, color: 'text-blue-600',    bg: 'bg-blue-50' },
  started:    { icon: <Loader2 size={12} className="animate-spin" />, color: 'text-blue-600',    bg: 'bg-blue-50' },
  completed:  { icon: <CheckCircle size={12} />,                      color: 'text-green-600',   bg: 'bg-green-50' },
  failed:     { icon: <XCircle size={12} />,                          color: 'text-red-600',     bg: 'bg-red-50' },
  cancelled:  { icon: <XCircle size={12} />,                          color: 'text-neutral-500', bg: 'bg-neutral-50' },
  'timed-out': { icon: <Clock size={12} />,                           color: 'text-amber-600',   bg: 'bg-amber-50' },
  initiated:  { icon: <Clock size={12} />,                            color: 'text-neutral-400', bg: 'bg-neutral-50' },
  unknown:    { icon: <Clock size={12} />,                            color: 'text-neutral-400', bg: 'bg-neutral-50' },
};

function formatDuration(ms?: number): string {
  if (!ms) return '\u2014';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const min = Math.floor(ms / 60_000);
  const sec = Math.round((ms % 60_000) / 1000);
  return `${min}m${sec}s`;
}

function shortenWorkflowType(type: string): string {
  // IssueLifecycleWorkflow -> IssueLifecycle
  return type.replace(/Workflow$/, '');
}

export default function WorkflowHierarchyTree({
  tree,
  selectedWorkflowId,
  onSelectWorkflow,
  className,
}: WorkflowHierarchyTreeProps) {
  const [expanded, setExpanded] = useState(true);

  const hasChildren = tree.children.length > 0;

  return (
    <div className={`bg-white rounded-lg border border-neutral-200 shadow-warm-sm p-4 ${className || ''}`}>
      <div className="flex items-center gap-2 mb-3">
        <GitBranch size={14} className="text-coral-500" />
        <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">Workflow Tree</p>
        {hasChildren && (
          <span className="text-[10px] font-mono text-neutral-400">
            {tree.children.length} child{tree.children.length !== 1 ? 'ren' : ''}
          </span>
        )}
      </div>

      {/* Root node */}
      <TreeNode
        node={tree}
        isRoot
        isSelected={selectedWorkflowId === tree.workflowId}
        isExpanded={expanded}
        onToggle={() => setExpanded(v => !v)}
        onSelect={() => onSelectWorkflow(tree.workflowId)}
      />

      {/* Children */}
      {expanded && hasChildren && (
        <div className="ml-4 mt-1 border-l border-neutral-200 pl-3 space-y-0.5">
          {tree.children.map(child => (
            <TreeNode
              key={child.workflowId}
              node={child}
              isSelected={selectedWorkflowId === child.workflowId}
              onSelect={() => onSelectWorkflow(child.workflowId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Tree Node ───────────────────────────────────────────────────── */

function TreeNode({
  node,
  isRoot,
  isSelected,
  isExpanded,
  onToggle,
  onSelect,
}: {
  node: ChildWorkflowNode;
  isRoot?: boolean;
  isSelected: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  onSelect: () => void;
}) {
  const hasChildren = node.children.length > 0;
  const statusCfg = STATUS_CONFIG[node.status] || STATUS_CONFIG.unknown;

  return (
    <button
      onClick={onSelect}
      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left transition-all group
        ${isSelected
          ? 'bg-coral-50 border border-coral-200 shadow-sm'
          : 'hover:bg-neutral-50 border border-transparent'
        }`}
    >
      {/* Expand/collapse toggle for root */}
      {isRoot && hasChildren ? (
        <button
          onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
          className="p-0.5 text-neutral-400 hover:text-neutral-600 shrink-0"
        >
          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
      ) : (
        !isRoot && <span className="w-4 shrink-0" />
      )}

      {/* Status icon */}
      <div className={`flex items-center justify-center w-5 h-5 rounded ${statusCfg.bg} ${statusCfg.color} shrink-0`}>
        {statusCfg.icon}
      </div>

      {/* Workflow info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-[11px] font-semibold truncate ${isSelected ? 'text-coral-700' : 'text-neutral-700'}`}>
            {shortenWorkflowType(node.workflowType)}
          </span>
          {isRoot && (
            <span className="text-[8px] font-bold uppercase tracking-wider text-coral-500 bg-coral-50 px-1 py-0.5 rounded shrink-0">
              Root
            </span>
          )}
        </div>
        <div className="text-[9px] font-mono text-neutral-400 truncate">
          {node.workflowId}
        </div>
      </div>

      {/* Metrics */}
      <div className="flex items-center gap-3 shrink-0">
        {node.activityCount > 0 && (
          <div className="flex items-center gap-1 text-[9px] text-neutral-400">
            <Activity size={10} />
            <span className="font-mono">{node.activityCount}</span>
          </div>
        )}
        <span className="text-[9px] font-mono text-neutral-400 tabular-nums min-w-[40px] text-right">
          {formatDuration(node.durationMs)}
        </span>
        <span className={`text-[9px] font-semibold capitalize ${statusCfg.color}`}>
          {node.status}
        </span>
      </div>
    </button>
  );
}
